import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { StatusGrid } from "../components/organisms/StatusGrid";
import { MaintenanceSection } from "../components/organisms/MaintenanceSection";
import { AksExplorerDialog } from "../components/organisms/AksExplorerDialog";
import { useStatusData } from "../hooks/useStatusData";
import type { MaintenanceWindow, MaintenanceAffectedService } from "../types/incidents";
import { apiFetch } from "../lib/config";

function formatRelativeTime(date: Date): string {
  const diff = Math.round((Date.now() - date.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (diff < 60) return rtf.format(-diff, "second");
  if (diff < 3600) return rtf.format(-Math.round(diff / 60), "minute");
  return rtf.format(-Math.round(diff / 3600), "hour");
}

const STATUS_CONFIG = {
  operational: {
    bg: "#107c10",
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <circle cx="16" cy="16" r="15" stroke="white" strokeWidth="2" fill="rgba(255,255,255,0.15)" />
        <path d="M9 16.5l5 5 9-10" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    headline: "All services are operating normally",
  },
  degraded: {
    bg: "#8a6502",
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <circle cx="16" cy="16" r="15" stroke="white" strokeWidth="2" fill="rgba(255,255,255,0.15)" />
        <path d="M16 9v9M16 22v1" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    ),
    headline: "Some services are experiencing degraded performance",
  },
  outage: {
    bg: "#a4262c",
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <circle cx="16" cy="16" r="15" stroke="white" strokeWidth="2" fill="rgba(255,255,255,0.15)" />
        <path d="M10 10l12 12M22 10L10 22" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    ),
    headline: "Service disruption detected — our team is investigating",
  },
  maintenance: {
    bg: "#1a56aa",
    icon: (
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <circle cx="16" cy="16" r="15" stroke="white" strokeWidth="2" fill="rgba(255,255,255,0.15)" />
        <path d="M16 9v7l4 4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    headline: "Scheduled maintenance in progress",
  },
};

function formatMaintEnd(iso: string): string {
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  if (diff <= 0) return "ending soon";
  if (diff < 3600) return `ends in ${Math.round(diff / 60)} min`;
  if (diff < 86400) return `ends in ${Math.round(diff / 3600)} h`;
  return `ends ${new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`;
}

function groupAffected(services: MaintenanceAffectedService[]): string {
  if (!services?.length) return "";
  const names = [...new Set(services.map(s => s.component_name ?? s.service_name))];
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 3).join(", ")} +${names.length - 3} more`;
}

type ServiceTab = "aptean" | "thirdparty";

const REFRESH_OPTIONS = [
  { label: "2 minutes",  ms: 2  * 60 * 1000 },
  { label: "5 minutes",  ms: 5  * 60 * 1000 },
  { label: "10 minutes", ms: 10 * 60 * 1000 },
];

interface ThirdPartyServiceItem {
  id: string;
  name: string;
  region: string;
  status: string;
  status_url: string;
  health_url: string;
  last_health_response: Record<string, unknown> | null;
  last_health_checked_at: string | null;
  last_health_error: string;
}

export function StatusPage() {
  const [refreshInterval, setRefreshInterval] = useState(REFRESH_OPTIONS[0].ms);
  const { grid, summary, lastUpdated, error } = useStatusData(refreshInterval);
  const [, forceTickUpdate] = useState(0);
  const [activeMaintenance, setActiveMaintenance] = useState<MaintenanceWindow[]>([]);
  const [activeTab, setActiveTab] = useState<ServiceTab>("aptean");
  const [thirdPartyServices, setThirdPartyServices] = useState<ThirdPartyServiceItem[]>([]);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  type AksStatus = "operational" | "degraded" | "outage" | "maintenance";
  const [aksExplorer, setAksExplorer] = useState<AksStatus | null>(null);

  useEffect(() => {
    apiFetch("/api/v1/maintenance/")
      .then((r) => r.json())
      .then((data) => setActiveMaintenance(data.active ?? []))
      .catch(() => null);
  }, []);

  useEffect(() => {
    apiFetch("/api/v1/status/third-party/")
      .then((r) => r.json())
      .then((data) => setThirdPartyServices(data))
      .catch(() => null);
  }, []);

  // Tick every second so "Updated X seconds ago" stays current
  useEffect(() => {
    const id = setInterval(() => forceTickUpdate((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const overallStatus = summary?.overall ?? "operational";
  const cfg = STATUS_CONFIG[overallStatus] ?? STATUS_CONFIG.operational;
  const serviceNames = grid ? [...new Set(grid.services.map((s) => s.name))] : [];

  // Compute status counts from the grid data
  const statusCounts = React.useMemo(() => {
    let operational = 0;
    let degraded = 0;
    let outage = 0;
    let maintenance = 0;
    if (grid) {
      for (const svc of grid.services) {
        for (const region of (svc.regions ?? [])) {
          if (region.status === "operational") operational++;
          else if (region.status === "degraded") degraded++;
          else if (region.status === "outage") outage++;
          else if (region.status === "maintenance") maintenance++;
        }
        for (const comp of (svc.components ?? [])) {
          for (const region of (comp.regions ?? [])) {
            if (region.status === "operational") operational++;
            else if (region.status === "degraded") degraded++;
            else if (region.status === "outage") outage++;
            else if (region.status === "maintenance") maintenance++;
          }
        }
      }
    }
    return { operational, degraded, outage, maintenance };
  }, [grid]);

  // Filter grid services by selected service sub-tab
  const filteredGridServices = React.useMemo(() => {
    if (!grid) return [];
    if (!selectedService) return grid.services;
    return grid.services.filter((s) => s.name === selectedService);
  }, [grid, selectedService]);

  return (
    <div className="sp-root">
      {/* ── Top navigation bar ── */}
      <nav className="sp-nav" aria-label="Site navigation">
        <div className="sp-nav__inner">
          <div className="sp-nav__brand">
            <img
              className="sp-nav__logo"
              src="https://stshrnextgenstorage.blob.core.windows.net/aptean-theme/Aptean-logo-login.png"
              alt="Aptean"
            />
            <span className="sp-nav__separator" aria-hidden="true" />
            <span className="sp-nav__page-name">StatusCentral</span>
          </div>
          <div className="sp-nav__actions">
            <Link to="/subscribe" className="sp-nav__link">Subscribe</Link>
          </div>
        </div>
      </nav>

      {/* ── Status count circles ── */}
      <div className="sp-counts" role="banner" aria-live="polite">
        <div className="sp-counts__inner">
          {([
            { key: "operational", label: "Operational" },
            { key: "degraded",    label: "Degraded" },
            { key: "outage",      label: "Outage" },
            { key: "maintenance", label: "Maintenance" },
          ] as const).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className="sp-counts__card"
              onClick={() => setAksExplorer(key)}
              title={`View ${label} pods in AKS`}
              style={{ cursor: "pointer" }}
            >
              <div className={`sp-counts__circle sp-counts__circle--${key}`}>
                <span className="sp-counts__number">{statusCounts[key]}</span>
              </div>
              <span className="sp-counts__label">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── AKS Explorer dialog ── */}
      {aksExplorer && (
        <AksExplorerDialog
          status={aksExplorer}
          onClose={() => setAksExplorer(null)}
        />
      )}

      {activeMaintenance.length > 0 && (
        <div className="sp-maintenance-banner" role="region" aria-label="Active maintenance">
          <div className="sp-maintenance-banner__inner">
            <div className="sp-maintenance-banner__icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2a8 8 0 100 16A8 8 0 0010 2zm0 4v5l3 3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="sp-maintenance-banner__content">
              <span className="sp-maintenance-banner__label">Maintenance in Progress</span>
              <ul className="sp-maintenance-banner__list">
                {activeMaintenance.map((w) => (
                  <li key={w.id} className="sp-maintenance-banner__item">
                    <strong>{w.title}</strong>
                    {w.description && (
                      <span className="sp-maintenance-banner__desc"> — {w.description}</span>
                    )}
                    {groupAffected(w.affected_services) && (
                      <span className="sp-maintenance-banner__affected">
                        {" "}· {groupAffected(w.affected_services)}
                      </span>
                    )}
                    <span className="sp-maintenance-banner__ends"> ({formatMaintEnd(w.ends_at)})</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="sp-error" role="alert">
          <strong>Unable to load status data.</strong> {error}
        </div>
      )}

      {/* ── Main content ── */}
      <main className="sp-main">

        {/* Service health — tabbed */}
        <section className="sp-section sp-section--tabs" aria-label="Service health">
          {/* Status legend + refresh bar */}
          <div className="sp-legend-bar">
            <div className="sp-refresh-bar">
              {lastUpdated && (
                <span className="sp-refresh-bar__updated">
                  Updated <span style={{ color: "var(--aptean-accent, #54B3BE)", fontWeight: 500 }}>{formatRelativeTime(lastUpdated)}</span>
                </span>
              )}
              <span className="sp-refresh-bar__sep" aria-hidden="true">|</span>
              <label className="sp-refresh-bar__label" htmlFor="refresh-interval">Refresh every</label>
              <select
                id="refresh-interval"
                className="sp-refresh-bar__select"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
              >
                {REFRESH_OPTIONS.map((opt) => (
                  <option key={opt.ms} value={opt.ms}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="sp-legend" aria-label="Status legend">
              <span className="sp-legend__item">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ display: "inline-block", verticalAlign: "middle" }}>
                  <circle cx="8" cy="8" r="8" fill="#107c10" />
                  <path d="M4.5 8.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Operational
              </span>
              <span className="sp-legend__item">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ display: "inline-block", verticalAlign: "middle" }}>
                  <circle cx="8" cy="8" r="8" fill="#d17a00" />
                  <path d="M8 4.5v4M8 10.5v1" stroke="white" strokeWidth="1.75" strokeLinecap="round" />
                </svg>
                Degraded
              </span>
              <span className="sp-legend__item">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ display: "inline-block", verticalAlign: "middle" }}>
                  <circle cx="8" cy="8" r="8" fill="#a4262c" />
                  <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="white" strokeWidth="1.75" strokeLinecap="round" />
                </svg>
                Outage
              </span>
              <span className="sp-legend__item"><span className="sp-dot sp-dot--maintenance" />Maintenance</span>
              <span className="sp-legend__item"><span className="sp-dot sp-dot--unknown" />Not Applicable</span>
            </div>
          </div>

          {/* Tab bar */}
          <div className="sp-tabs" role="tablist" aria-label="Service health categories">
            {(
              [
                { id: "aptean",     label: "Aptean Service Health" },
                { id: "thirdparty", label: "AI Services Health" },
              ] as { id: ServiceTab; label: string }[]
            ).map(({ id, label }) => (
              <button
                key={id}
                role="tab"
                type="button"
                className={`sp-tab${activeTab === id ? " sp-tab--active" : ""}`}
                aria-selected={activeTab === id}
                onClick={() => setActiveTab(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab panels */}
          <div role="tabpanel">
            {activeTab === "aptean" && (
              <>
                {/* Service sub-tabs */}
                {serviceNames.length > 1 && (
                  <div className="sp-service-tabs" role="tablist" aria-label="Service filter">
                    <button
                      type="button"
                      role="tab"
                      className={`sp-service-tab${selectedService === null ? " sp-service-tab--active" : ""}`}
                      aria-selected={selectedService === null}
                      onClick={() => setSelectedService(null)}
                    >
                      All Services
                    </button>
                    {serviceNames.map((name) => (
                      <button
                        key={name}
                        type="button"
                        role="tab"
                        className={`sp-service-tab${selectedService === name ? " sp-service-tab--active" : ""}`}
                        aria-selected={selectedService === name}
                        onClick={() => setSelectedService(name)}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}

                {grid ? (
                  <StatusGrid services={filteredGridServices} />
                ) : !error ? (
                  <div className="sp-skeleton sp-skeleton--grid" aria-label="Loading service status" />
                ) : null}
              </>
            )}

            {activeTab === "thirdparty" && (
              <div className="sp-thirdparty">
                {thirdPartyServices.length === 0 ? (
                  <div className="sp-tab-placeholder">
                    <p className="sp-tab-placeholder__title">No third-party services configured</p>
                    <p className="sp-tab-placeholder__body">Third-party service health will appear here once added by an administrator.</p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    {thirdPartyServices.map((svc) => {
                      const healthResp = svc.last_health_response;
                      const AI_SERVICE_KEYS = new Set(["azureopenai", "vertexai", "sessionpool"]);
                      const isUsGov = svc.region?.toLowerCase().includes("us gov");
                      const subServices =
                        healthResp &&
                        typeof healthResp.services === "object" &&
                        healthResp.services !== null
                          ? Object.entries(healthResp.services as Record<string, { status: string }>)
                              .filter(([name]) => {
                                const key = name.toLowerCase();
                                if (key === "sessionpool" && isUsGov) return false;
                                return AI_SERVICE_KEYS.has(key);
                              })
                          : null;
                      const defaultModel =
                        healthResp && typeof healthResp.default_model === "string"
                          ? healthResp.default_model
                          : null;
                      const overallOk = svc.status === "operational";

                      return (
                        <div
                          key={svc.id}
                          style={{
                            border: "1px solid var(--aptean-border, #e5e7eb)",
                            borderRadius: 8,
                            overflow: "hidden",
                          }}
                        >
                          {/* Card header — service name + overall status */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "0.75rem 1.25rem",
                              background: "var(--aptean-surface-2, #f9fafb)",
                              borderBottom: "1px solid var(--aptean-border, #e5e7eb)",
                            }}
                          >
                            <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                              {svc.name}{svc.region ? ` — ${svc.region}` : ""}
                            </span>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                              <span
                                className={`sp-status-icon sp-status-icon--${svc.status}`}
                                aria-hidden="true"
                              />
                              <span
                                style={{
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  textTransform: "capitalize",
                                  color: overallOk
                                    ? "var(--aptean-success, #107c10)"
                                    : "var(--aptean-danger, #a4262c)",
                                }}
                              >
                                {svc.status}
                              </span>
                            </div>
                          </div>

                          {/* Card body */}
                          <div style={{ padding: "0.75rem 1.25rem" }}>
                            {svc.last_health_error ? (
                              <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--aptean-danger, #a4262c)" }}>
                                {svc.last_health_error}
                              </p>
                            ) : subServices ? (
                              <>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                                  <thead>
                                    <tr>
                                      <th
                                        style={{
                                          textAlign: "left",
                                          fontWeight: 500,
                                          color: "var(--aptean-muted, #888)",
                                          paddingBottom: "0.4rem",
                                          borderBottom: "1px solid var(--aptean-border, #e5e7eb)",
                                        }}
                                      >
                                        Service
                                      </th>
                                      <th
                                        style={{
                                          textAlign: "right",
                                          fontWeight: 500,
                                          color: "var(--aptean-muted, #888)",
                                          paddingBottom: "0.4rem",
                                          borderBottom: "1px solid var(--aptean-border, #e5e7eb)",
                                        }}
                                      >
                                        Status
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {subServices.map(([name, data]) => {
                                      const isOk = data.status === "ok";
                                      return (
                                        <tr key={name}>
                                          <td
                                            style={{
                                              padding: "0.4rem 0",
                                              borderBottom: "1px solid var(--aptean-border, #e5e7eb)",
                                              textTransform: "capitalize",
                                            }}
                                          >
                                            {name}
                                          </td>
                                          <td
                                            style={{
                                              padding: "0.4rem 0",
                                              borderBottom: "1px solid var(--aptean-border, #e5e7eb)",
                                              textAlign: "right",
                                            }}
                                          >
                                            <div style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                                              <span
                                                className={`sp-status-icon sp-status-icon--${isOk ? "operational" : "degraded"}`}
                                                aria-hidden="true"
                                              />
                                              <span
                                                style={{
                                                  fontSize: "0.78rem",
                                                  color: isOk
                                                    ? "var(--aptean-success, #107c10)"
                                                    : "var(--aptean-danger, #a4262c)",
                                                }}
                                              >
                                                {isOk ? "Operational" : data.status}
                                              </span>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                  {defaultModel && (
                                    <tfoot>
                                      <tr>
                                        <td
                                          style={{
                                            padding: "0.4rem 0",
                                            textTransform: "capitalize",
                                          }}
                                        >
                                          Default model
                                        </td>
                                        <td
                                          style={{
                                            padding: "0.4rem 0",
                                            textAlign: "right",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {defaultModel}
                                        </td>
                                      </tr>
                                    </tfoot>
                                  )}
                                </table>
                              </>
                            ) : (
                              <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--aptean-muted, #888)" }}>
                                No health data yet. Run a health check from the admin panel.
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}


          </div>
        </section>

        {/* Scheduled maintenance */}
        <section className="sp-section" aria-labelledby="maintenance-heading">
          <h2 id="maintenance-heading" className="sp-section__title">Scheduled Maintenance</h2>
          <MaintenanceSection />
        </section>

      </main>

      {/* ── Footer ── */}
      <footer className="sp-footer">
        <div className="sp-footer__inner">
          <span>© {new Date().getFullYear()} Aptean. All rights reserved.</span>
          <nav className="sp-footer__links" aria-label="Footer links">
            <a href="https://www.aptean.com" className="sp-footer__link" target="_blank" rel="noopener noreferrer">aptean.com</a>
            <Link to="/admin/login" className="sp-footer__link">Admin Portal</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
